import { useState } from 'react';
import { darkTheme } from '../../../lib/tokens';
import type { TopicMasteryItem } from './api';

interface Props {
  items: TopicMasteryItem[];
}

const COLLAPSED_LIMIT = 12;

export function TopicMasterySection({ items }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const sorted = [...items].sort((a, b) => b.masteryPercent - a.masteryPercent);
  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_LIMIT);
  const hidden = sorted.length - visible.length;

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">Topic-Mastery</h3>
        <span className="text-[11px] text-text-tertiary">
          40% Lessons · 40% Quiz · 20% Karteikarten
        </span>
      </div>

      {sorted.length === 0 ? (
        <p className="text-xs text-text-tertiary">Noch keine Topic-Daten.</p>
      ) : (
        <div className="space-y-2">
          {visible.map((t) => (
            <Row key={t.topicId} item={t} />
          ))}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-2 text-xs text-accent-light hover:underline"
            >
              {hidden} weitere Topics anzeigen
            </button>
          )}
          {expanded && sorted.length > COLLAPSED_LIMIT && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="mt-2 text-xs text-accent-light hover:underline"
            >
              Einklappen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ item }: { item: TopicMasteryItem }): JSX.Element {
  const barColor = barColorFor(item.masteryPercent);
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3">
      <div className="min-w-0">
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <span className="truncate text-sm text-text-primary">{item.topicTitle}</span>
          <span className="shrink-0 text-xs text-text-tertiary">
            {item.lessonsCompleted}/{item.lessonsTotal}
            {item.avgQuizScore > 0 && ` · Quiz Ø ${item.avgQuizScore}%`}
          </span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full"
          style={{ background: darkTheme.colors.bgElevated }}
        >
          <div
            style={{
              width: `${Math.max(item.masteryPercent, 2)}%`,
              height: '100%',
              background: barColor,
              transition: 'width 250ms ease',
            }}
          />
        </div>
      </div>
      <div className="w-12 text-right text-sm font-semibold tabular text-text-primary">
        {item.masteryPercent}%
      </div>
    </div>
  );
}

function barColorFor(pct: number): string {
  if (pct >= 75) return darkTheme.colors.success;
  if (pct >= 40) return darkTheme.colors.accent;
  if (pct >= 15) return darkTheme.colors.warning;
  return darkTheme.colors.danger;
}
